import '@repo/tailwind-config/styles'
import '../i18n'

import * as Sentry from '@sentry/react'
import { TanStackDevtools } from '@tanstack/react-devtools'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { Toaster } from 'sonner'

import App from './App'
import AuthProvider from './lib/contexts/auth'
import DownloadDocumentsProvider from './lib/contexts/download-documents'
import LoadingProvider from './lib/contexts/loading'
import { ObserverProvider } from './lib/contexts/observer'
import { SelectedEnrollmentProvider } from './lib/contexts/selected-enrollment'
import SidebarManagerProvider from './lib/contexts/sidebar-manager'
import TrackProgressBatchesProvider from './lib/contexts/track-progress-batches'
import {
  isFetchRequestError,
  shouldFilterSentryFetchError
} from './lib/utils/functions/fetch-error'
import { isBrowserExtensionSentryEvent } from './lib/utils/functions/sentry-event'

if (import.meta.env.MODE === 'production')
  Sentry.init({
    dsn: 'https://6925d74406b403ad9be9d336f5c4bdfe@o1180561.ingest.us.sentry.io/4507379870662656',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],

    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', 'lec-staging.com'],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
      const error = hint.originalException

      if (shouldFilterSentryFetchError(error)) return null
      if (isBrowserExtensionSentryEvent(event)) return null

      if (isFetchRequestError(error)) {
        event.tags = {
          ...event.tags,
          app_fetch_error_kind: error.kind,
          app_online_status: String(error.isOnline)
        }

        event.contexts = {
          ...event.contexts,
          app_fetch_request: {
            url: error.request.url,
            method: error.request.method,
            originalMessage: error.originalMessage
          }
        }
      }

      return event
    }
  })

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false },
    mutations: { retry: false }
  }
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <QueryClientProvider client={queryClient}>
    <ObserverProvider>
      <LoadingProvider>
        <AuthProvider>
          <DownloadDocumentsProvider>
            <TrackProgressBatchesProvider>
              <BrowserRouter>
                <SelectedEnrollmentProvider>
                  <SidebarManagerProvider>
                    <App />
                    {import.meta.env.DEV && (
                      <TanStackDevtools
                        plugins={[
                          {
                            name: 'TanStack Query',
                            render: <ReactQueryDevtoolsPanel />
                          }
                        ]}
                      />
                    )}

                    <Toaster position={'top-right'} pauseWhenPageIsHidden richColors />
                  </SidebarManagerProvider>
                </SelectedEnrollmentProvider>
              </BrowserRouter>
            </TrackProgressBatchesProvider>
          </DownloadDocumentsProvider>
        </AuthProvider>
      </LoadingProvider>
    </ObserverProvider>
  </QueryClientProvider>
)
